import {
  Box,
  Text,
  Tooltip,
  Modal,
  LoadingOverlay,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { ReusableTable } from "../../Shared/table";
import { useNavigate } from "react-router-dom";
import {
  statusColors,
} from "../../../constants/color-constants";
import { useEffect, useState, useRef, useMemo } from "react";
import FilterComponent from "../../Filter";
import PaginationComponent from "../../Shared/Pagination";
import { transformApiDataToMyQueries } from "../../../utils/data-transform-utils";
import { fetchHtmlUrlQuery, fetchPdfUrlQuery } from "../../../hooks/queries";
import { convertToSelectOptions } from "../../../utils";
import { RouteConstants } from "../../../constants/route-constant";
import { HtmlViewer } from "../../Shared/HtmlViewer";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { querySubmittedStates } from "../../../constants/app-constant";
import { setMyQueriesCount } from "../../../store/providerReducer";
import {
  convertCustomStatusToActual,
  createCustomStatusOptions,
} from "../../../utils/status-filter-utils";
import {
  useQueryList,
  useStatusList,
  useTopicList,
} from "../../../hooks/queries";

export interface MyQueryData {
  id: string;
  patientName: string;
  patientMRN: string;
  topic: string;
  status: string;
  statusId: string | number; ////STATUS CHANGES
  ageing: any;
  queryBy: string;
  queryDate: string;
  isEditable: string;
  createdFormatTime?: string;
  updatedFormatTime?: string;
}

interface PdfResponse {
  blobData: Blob;
  filename: string;
  contentType: string;
  size: number;
}

// Column definitions for the my queries table

const limit = 15;

export default function MyQueries() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
  const provider = useAppSelector((state) => state?.provider?.provider);
  const providerNPI = useAppSelector((state) => state?.provider?.providerNPI);
  // Modal state
  const [opened, { open, close }] = useDisclosure(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [, setPdfError] = useState<string>("");
  const [downloadingRowId, setDownloadingRowId] = useState<string | null>(null);
  const [printingRowId, setPrintingRowId] = useState<string | null>(null);
  const [rowClickLoading, setRowClickLoading] = useState(false);
  const [patientQueryData, setPatientQueryData] = useState<MyQueryData[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [rowStatus, setRowStatus] = useState<any>();
  const [rowID, setRowID] = useState<any>();
  const [htmlData, setHtmlData] = useState<any>("");

  const [customStatusFilter, setCustomStatusFilter] = useState<string[]>([
    "open_and_in_progress",
  ]);
  const [topicFilter, setTopicFilter] = useState<string[]>([]);
  const [queryTypeFilter, setQueryTypeFilter] = useState<string[]>(["all"]);
  const [search, setSearch] = useState<string>("");
  const processingRef = useRef(false);

  const [modalDownloading, setModalDownloading] = useState(false);
  const [modalPrint, setModalPrint] = useState(false);

  const [originalStatusOptions, setOriginalStatusOptions] = useState<any[]>([]);
  const [customStatusOptions, setCustomStatusOptions] = useState<any[]>([]);
  const [statusMapping, setStatusMapping] = useState<any>({});
  const [topicOptions, setTopicOptions] = useState<any[]>([]);
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  const { data: statusResponse, isLoading: statusLoading } = useStatusList();
  const { data: topicResponse, isLoading: topicLoading } = useTopicList();

  useEffect(() => {
    if (statusResponse?.data) {
      const originalOptions = convertToSelectOptions(statusResponse.data);
      setOriginalStatusOptions(originalOptions);
      const { customStatusOptions: nextCustomOptions, statusMapping: mapping } =
        createCustomStatusOptions(originalOptions);
      setCustomStatusOptions(nextCustomOptions);
      setStatusMapping(mapping);
      setCustomStatusFilter(["open_and_in_progress"]);
      setFiltersInitialized(true);
    }
  }, [statusResponse]);

  useEffect(() => {
    if (topicResponse?.data) {
      const options = convertToSelectOptions(topicResponse.data);
      const allTopicValues = options?.map((option: any) => option.value);
      setTopicFilter(allTopicValues);
      setTopicOptions(options);
      setFiltersInitialized(true);
    }
  }, [topicResponse]);

  const queryListParams = useMemo(() => {
    const actualStatusFilter = convertCustomStatusToActual(
      customStatusFilter,
      statusMapping
    );

    return {
      status_id:
        actualStatusFilter?.length > 0 ? actualStatusFilter.join(",") : "",
      topic_id: topicFilter?.length > 0 ? topicFilter.join(",") : "",
      query_type: queryTypeFilter?.length > 0 ? queryTypeFilter[0] : "",
      provider_npi: providerNPI,
      filter_id: "",
      start_date: "",
      end_date: "",
      limit,
      page: page - 1,
      search,
    };
  }, [
    customStatusFilter,
    page,
    providerNPI,
    queryTypeFilter,
    search,
    statusMapping,
    topicFilter,
  ]);

  const {
    data: queryListResponse,
    isLoading: patientQueryLoading,
    isFetching,
  } = useQueryList(queryListParams, {
    enabled: filtersInitialized && Boolean(providerNPI),
    polling: true,
  });

  useEffect(() => {
    if (!providerNPI) {
      setPatientQueryData([]);
      setTotalPages(0);
      return;
    }

    if (queryListResponse?.data?.data) {
      setTotalPages(Math.ceil(queryListResponse.data.total_count / limit));
      const transformedData = transformApiDataToMyQueries(
        queryListResponse.data,
        originalStatusOptions,
        provider?.timezone_info
      );
      setPatientQueryData(transformedData);

      const isDefaultOpenFilter =
        customStatusFilter.length === 1 &&
        customStatusFilter[0] === "open_and_in_progress";
      if (isDefaultOpenFilter) {
        dispatch(setMyQueriesCount(queryListResponse.data.total_count ?? 0));
      }
    } else if (queryListResponse && !queryListResponse.data?.data) {
      setPatientQueryData([]);
      setTotalPages(0);
    }
  }, [
    customStatusFilter,
    dispatch,
    originalStatusOptions,
    provider?.timezone_info,
    providerNPI,
    queryListResponse,
  ]);

  const initialLoading =
    (statusLoading || topicLoading) && !filtersInitialized;
  const isRefreshing = isFetching && !patientQueryLoading && !initialLoading;

  // Static options for query type dropdown
  const queryTypeOptions = [{ value: "all", label: " time one" }];

  const myQueriesColumns = [
    {
      header: "Patient Name / MRN",
      align: "left" as const,
      accessor: (row: MyQueryData) => (
        <Tooltip
          arrowPosition='side'
          arrowOffset={5}
          arrowSize={5}
          arrowRadius={3}
          label={
            <div>
              <div>{row.patientName}</div>
              <div>{row.patientMRN}</div>
            </div>
          }
          position='bottom-start'
          withArrow
          multiline
        >
          <div>
            <Text size={isSmallScreen ? "12px" : "14px"}>
              {row.patientName}
            </Text>
            <Text size='xs' c='dimmed' mt={"2px"}>
              {row.patientMRN}
            </Text>
          </div>
        </Tooltip>
      ),
      width: "200px",
    },
    {
      header: "Topic",
      align: "left" as const,
      accessor: "topic" as keyof MyQueryData,
      width: "200px",
    },
    {
      header: "Status",
      align: "left" as const,
      accessor: "status" as keyof MyQueryData,
      width: "120px",
      cellType: "status" as const,
    },
    {
      header: "Ageing",
      align: "left" as const,
      accessor: "ageing" as keyof MyQueryData,
      width: "120px",
      cellType: "aging" as const,
    },
    {
      header: "Query By / Date",
      align: "left" as const,
      accessor: (row: MyQueryData) => (
        <Tooltip
          arrowPosition='side'
          arrowOffset={5}
          arrowSize={5}
          arrowRadius={3}
          label={
            <div style={{ textAlign: "left" }}>
              <div> {row.queryBy}</div>
              <div>{`${row?.queryDate}, ${row?.createdFormatTime}`}</div>
              <div>{`${row?.id}`}</div>
            </div>
          }
          position='bottom-start'
          withArrow
          multiline
        >
          <div>
            <Text size={isSmallScreen ? "12px" : "14px"}>{row.queryBy}</Text>
            <Text size='xs' c='dimmed' mt={"2px"}>
              {row.queryDate}
            </Text>
          </div>
        </Tooltip>
      ),
      width: "200px",
    },
  ];

  // Utility function to download PDF
  const downloadPdf = async (blob: Blob, filename?: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "document.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  // Utility function to check if running in Epic Hyperspace
  const isEpicHyperspace = (): boolean => {
    return !!(
      (window as any).Epic ||
      (window as any).EpicWeb ||
      window.self !== window.top ||
      window.location.href.includes("epic") ||
      window.location.href.includes("hyperspace")
    );
  };

  // Utility function to open PDF in external window/browser
  const openPdfInExternalWindow = (blob: Blob, filename?: string) => {
    const url = URL.createObjectURL(blob);

    try {
      if (isEpicHyperspace()) {
        try {
          if (
            (window as any).Epic?.Clinical?.Informatics?.Web?.OpenExternalWindow
          ) {
            (window as any).Epic.Clinical.Informatics.Web.OpenExternalWindow(
              url
            );
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            return;
          }
        } catch (error) {
          console.warn(
            "Epic OpenExternalWindow method failed, fallback to window.open:",
            error
          );
        }
      }

      const externalWindow = window.open(
        url,
        "_blank",
        "width=1024,height=768,scrollbars=yes,resizable=yes"
      );

      if (!externalWindow) {
        // Pop-up blocked → just alert or notify user
        console.error("Pop-up blocked or failed to open external window");
      }

      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.error("Failed to open PDF in external window:", error);
      // ❌ Removed downloadPdf fallback
      alert("Failed to open PDF. Please try again.");
      URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    if (!filtersInitialized) return;
    setPage(1);
  }, [customStatusFilter, topicFilter, queryTypeFilter, search, filtersInitialized]);

  // Cleanup PDF blob when modal closes
  useEffect(() => {
    return () => {
      if (pdfBlob) {
        URL.revokeObjectURL(URL.createObjectURL(pdfBlob));
      }
    };
  }, [pdfBlob]);

  const fetchPdfUrlFromArcPlus = async (
    id: string
  ): Promise<PdfResponse | null> => {
    try {
      const response = await fetchPdfUrlQuery(id);
      return response?.data || null;
    } catch (error) {
      console.error("Failed to fetch PDF URL:", error);
      return null;
    }
  };
  const fetchHtmlFromArcPlus = async (id: string): Promise<any | null> => {
    try {
      const response = await fetchHtmlUrlQuery(id);
      return response?.data || null;
    } catch (error) {
      console.error("Failed to fetch PDF URL:", error);
      return null;
    }
  };

  // Event handlers
  const handleEdit = (row: MyQueryData) => {
    navigate(RouteConstants.ROUTE_QUERY_DETAILS.replace(":query_id", row?.id), {
      state: {
        queryData: row,
        editMode: true,
        from: RouteConstants.ROUTE_MY_QUERIES,
      },
    });
  };

  const handleDownload = async (row: MyQueryData) => {
    try {
      setDownloadingRowId(row.id);

      const response = await fetchPdfUrlFromArcPlus(row?.id);

      if (!response) {
        console.error("No PDF response received for download");
        return;
      }

      const blobData = response?.blobData;
      const filename =
        response?.filename || `${row.patientName}_query_${row.id}.pdf`;
      const contentType = response?.contentType || "application/pdf";

      let actualBlob: Blob;

      if (blobData instanceof Blob) {
        actualBlob = blobData;
      } else if (typeof blobData === "string") {
        try {
          const stringData = blobData as string;
          const base64Data = stringData.includes(",")
            ? stringData.split(",")[1]
            : stringData;

          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          actualBlob = new Blob([bytes], { type: contentType });
        } catch (error) {
          console.error(
            "Failed to convert base64 to blob for download:",
            error
          );
          return;
        }
      } else {
        console.error("Invalid PDF data format for download");
        return;
      }

      if (actualBlob.size === 0) {
        console.error("Received empty PDF file for download");
        return;
      }

      await downloadPdf(actualBlob, filename);
      openPdfInExternalWindow(actualBlob, filename);
    } catch (error) {
      console.error("Error handling PDF download:", error);
    } finally {
      setDownloadingRowId(null);
    }
  };
  const handleDownloadModal = async (row: string) => {
    try {
      setModalDownloading(true);

      const response = await fetchPdfUrlFromArcPlus(row);

      if (!response) {
        console.error("No PDF response received for download");
        return;
      }

      const blobData = response?.blobData;
      const contentType = response?.contentType || "application/pdf";
      const filename = response?.filename || `patient_query_${row}.pdf`;

      let actualBlob: Blob;

      if (blobData instanceof Blob) {
        actualBlob = blobData;
      } else if (typeof blobData === "string") {
        try {
          const stringData = blobData as string;
          const base64Data = stringData.includes(",")
            ? stringData.split(",")[1]
            : stringData;

          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          actualBlob = new Blob([bytes], { type: contentType });
        } catch (error) {
          console.error(
            "Failed to convert base64 to blob for download:",
            error
          );
          return;
        }
      } else {
        console.error("Invalid PDF data format for download");
        return;
      }

      if (actualBlob.size === 0) {
        console.error("Received empty PDF file for download");
        return;
      }
      await downloadPdf(actualBlob, filename);
      openPdfInExternalWindow(actualBlob, filename);
      setModalDownloading(false);
    } catch (error) {
      console.error("Error handling PDF download:", error);
    } finally {
      setDownloadingRowId(null);
      setModalDownloading(false);
    }
  };

  const handlePrint = async (row: MyQueryData) => {
    try {
      setPrintingRowId(row.id);

      const response = await fetchPdfUrlFromArcPlus(row?.id);

      if (!response) {
        console.error("No PDF response received for print");
        return;
      }

      const blobData = response?.blobData;
      const filename =
        response?.filename || `${row.patientName}_query_${row.id}.pdf`;
      const contentType = response?.contentType || "application/pdf";

      let actualBlob: Blob;

      if (blobData instanceof Blob) {
        actualBlob = blobData;
      } else if (typeof blobData === "string") {
        try {
          const stringData = blobData as string;
          const base64Data = stringData.includes(",")
            ? stringData.split(",")[1]
            : stringData;

          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          actualBlob = new Blob([bytes], { type: contentType });
        } catch (error) {
          console.error("Failed to convert base64 to blob for print:", error);
          return;
        }
      } else {
        console.error("Invalid PDF data format for print");
        return;
      }

      if (actualBlob.size === 0) {
        console.error("Received empty PDF file for print");
        return;
      }

      openPdfInExternalWindow(actualBlob, filename);
    } catch (error) {
      console.error("Error handling PDF print:", error);
    } finally {
      setPrintingRowId(null);
    }
  };
  const handlePrintMOdal = async (row: string) => {
    try {
      setModalPrint(true);
      const response = await fetchPdfUrlFromArcPlus(row);

      if (!response) {
        console.error("No PDF response received for print");
        return;
      }

      const blobData = response?.blobData;
      const contentType = response?.contentType || "application/pdf";

      let actualBlob: Blob;

      if (blobData instanceof Blob) {
        actualBlob = blobData;
      } else if (typeof blobData === "string") {
        try {
          const stringData = blobData as string;
          const base64Data = stringData.includes(",")
            ? stringData.split(",")[1]
            : stringData;

          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          actualBlob = new Blob([bytes], { type: contentType });
        } catch (error) {
          console.error("Failed to convert base64 to blob for print:", error);
          return;
        }
      } else {
        console.error("Invalid PDF data format for print");
        return;
      }

      if (actualBlob.size === 0) {
        console.error("Received empty PDF file for print");
        return;
      }

      openPdfInExternalWindow(actualBlob);
      setModalPrint(false);
    } catch (error) {
      console.error("Error handling PDF print:", error);
    } finally {
      setPrintingRowId(null);
      setModalPrint(false);
    }
  };

  const handleRowClick = async (row: MyQueryData) => {
    if (processingRef.current) {
      console.log("BLOCKED: Already processing");
      return;
    }
    if (querySubmittedStates?.includes(Number(row?.statusId))) {
      try {
        setRowClickLoading(true);
        setPdfLoading(true);
        processingRef.current = true;

        setPdfError("");

        const responseHtml = await fetchHtmlFromArcPlus(row?.id);
        if (!responseHtml) {
          setPdfError("No HTML content received");
          return;
        }
        console.log(responseHtml?.data, "ssssss");
        setHtmlData(responseHtml?.data);

        setRowStatus(row?.status);
        setRowID(row?.id);

        open();

        open();
      } catch (error) {
        console.error("Error handling PDF preview:", error);
        setPdfError("An error occurred while processing the PDF");
      } finally {
        setPdfLoading(false);
        setRowClickLoading(false);
        processingRef.current = false; // Block immediately
      }
    } else {
      navigate(
        RouteConstants.ROUTE_QUERY_DETAILS.replace(":query_id", row?.id),
        {
          state: { queryData: row, from: RouteConstants.ROUTE_MY_QUERIES },
        }
      );
    }
  };

  const handleModalClose = () => {
    close();
    setPdfBlob(null);
    setPdfError("");
  };

  const handlePdfError = (error: string) => {
    setPdfError(error);
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <LoadingOverlay
        visible={rowClickLoading}
        zIndex={2000}
        overlayProps={{ radius: "sm", blur: 0.2 }}
        loaderProps={{ size: "lg" }}
      />
      <Box style={{ flexShrink: 0 }}>
        <FilterComponent
          loading={initialLoading}
          refreshing={isRefreshing}
          dropDown1Options={customStatusOptions}
          dropDown2Options={topicOptions}
          dropDown3Options={queryTypeOptions}
          dropdown1Values={customStatusFilter}
          setDropdown1Values={setCustomStatusFilter}
          dropdown2Values={topicFilter}
          setDropdown2Values={setTopicFilter}
          dropdown3Values={queryTypeFilter}
          setDropdown3Values={setQueryTypeFilter}
          searchValue={search}
          setSearchValue={setSearch}
          debounceDelay={500}
        />
      </Box>

      <ReusableTable
        showRowIdBelowActions={true}
        showSeriesNumber
        loading={patientQueryLoading || initialLoading}
        data={patientQueryData}
        columns={myQueriesColumns}
        getRowId={(row) => `${row?.id}`}
        onEdit={handleEdit}
        downloadingLoading={downloadingRowId}
        printingLoading={printingRowId}
        onDownload={handleDownload}
        onPrint={handlePrint}
        onRowClick={handleRowClick}
        statusColors={statusColors}
        canEdit={(row) => row.status === "Completed" && row?.isEditable === "Y"}
      />

      <Box
        style={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "flex-end",
          padding: "4px",
        }}
      >
        <PaginationComponent
          total={totalPages}
          value={page}
          onChange={setPage}
          disabled={patientQueryLoading}
        />
      </Box>

      <Modal
        opened={opened}
        onClose={handleModalClose}
        size='xl'
        centered
        styles={{
          body: { padding: 0 },
          header: { borderBottom: "1px solid #e9ecef" },
        }}
        withCloseButton={false}
      >
        <div style={{ height: "80vh" }}>
          <LoadingOverlay visible={pdfLoading} />
          <HtmlViewer
            htmlContent={htmlData}
            onError={handlePdfError}
            queryId={rowID}
            onDownload={() => handleDownloadModal(rowID)}
            onPrint={() => handlePrintMOdal(rowID)}
            onClose={handleModalClose}
            status={rowStatus}
            downloadLoading={modalDownloading}
            printLoading={modalPrint}
          />
        </div>
      </Modal>
    </div>
  );
}
